import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { provide } from '@lit/context'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { style } from './base-product-tile.style.js'

import { InternalProduct, ProductModel, TemplateProductModel } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { productTileIndexContext, productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { track } from 'components/shared/analytics/analytics.js'
import { ProductAddedEvent } from 'components/ordering/multiple-baskets/basket-events.js'
import { AreaPaddingCfg, CombinedLayoutConfig, DynamicSectionCfg, FlexDirection, ImageDisplayConfig, ImageSectionCfg, Place, SimpleLayoutCfg, SimpleWidgetCfg, ZoneCfg } from './base-product-tile-types.js'
import { buildWidgetElement } from './widget-renderer.js'
import { isWidgetVisible } from './widget-rules.js'
import { resolveLayoutConfig } from './layout-config.js'

export type { CombinedLayoutConfig, ImageDisplayConfig, ImageSectionCfg, Place, SimpleLayoutCfg, SimpleWidgetCfg } from './base-product-tile-types.js'

export class ProductTileFooterResizedEvent extends Event {
  static type = 'csc-product-tile-footer-resized' as const

  constructor(public heights: number[]) {
    super(ProductTileFooterResizedEvent.type, { bubbles: true, composed: true })
  }
}

declare global {
  interface GlobalEventHandlersEventMap {
    [ProductTileFooterResizedEvent.type]: ProductTileFooterResizedEvent,
  }
}

// Simple JSON model: section -> place -> widgets

const PLACES_9: Place[] = [
  'top-left','top-center','top-right',
  'middle-left','middle-center','middle-right',
  'bottom-left','bottom-center','bottom-right'
]
const PLACES_8: Exclude<Place, 'middle-center'>[] = PLACES_9.filter(p => p !== 'middle-center') as Exclude<Place, 'middle-center'>[]

const arraysEqual = (a: number[], b: number[] | null): boolean =>
  b !== null && a.length === b.length && a.every((value, i) => value === b[i])

export const toInternalProduct = (rawProduct: object): InternalProduct =>
  new ProductModel().with(rawProduct as Parameters<ProductModel['with']>[0]).parseToInternal()

export const tileConfigConverter = (tagName: string) => (value: string | null): CombinedLayoutConfig | SimpleLayoutCfg | null => {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch (error) {
    console.error(`<${tagName}> "tile-config" is not valid JSON, tiles will render without a config.`, error)
    return null
  }
}

initializeIconsByRole({
  'csc-product-tile.add-to-order-template': { name: 'star', style: 'light' },
})

@customElement('csc-base-product-tile')
export class CSCBaseProductTile extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'locale' })
  locale: string = 'en-US'

  @provide({ context: productTileProductContext })
  @property({ type: Object, converter: (value): InternalProduct => toInternalProduct(value ? JSON.parse(value) : {}) })
  product: InternalProduct = new ProductModel().parseToInternal()

  @property({ type: Object, attribute: 'tile-config', converter: tileConfigConverter('csc-base-product-tile') })
  tileConfig: SimpleLayoutCfg | CombinedLayoutConfig | null = null

  @property({ type: String, attribute: 'card-variant' })
  cardVariant: 'default' | 'wide' | 'inline' = 'default'

  @property({ type: Boolean, attribute: 'loading' })
  loading = false

  @state()
  private isMobile = false

  private mobileQuery: MediaQueryList | undefined

  private handleMobileQueryChange = (event: MediaQueryListEvent) => {
    this.isMobile = event.matches
  }

  @property({ type: Number, attribute: 'skeleton-height' })
  skeletonHeight: number | null = null

  @property({ type: String, attribute: 'skeleton-variant' })
  skeletonVariant: 'primary' | 'secondary' = 'secondary'

  @property({ type: Object, attribute: 'template-product', converter: (value): InternalProduct | null => {
    if (value) {
      const rawProduct = typeof value === 'string' ? JSON.parse(value) : value as Record<string, unknown>
      return new TemplateProductModel().with(rawProduct).parseToInternal()
    }
    return null
  } })
  templateProduct: InternalProduct | null = null

  @property({ type: String, attribute: 'template-product-url' })
  templateProductUrl: string = ''

  @provide({ context: productTileIndexContext })
  @property({ type: Number, attribute: 'index' })
  index?: number

  @property({ attribute: false })
  footerRowMinHeights: number[] | null = null

  override willUpdate(changedProperties: PropertyValues) {
    if (
      (changedProperties.has('templateProduct') || changedProperties.has('templateProductUrl')) &&
      this.templateProduct !== null
    ) {
      this.product = this.templateProductUrl
        ? { ...this.templateProduct, pageLink: this.templateProductUrl }
        : this.templateProduct
    }
  }

  override connectedCallback() {
    super.connectedCallback()
    this.mobileQuery = window.matchMedia(`(max-width: ${breakpoints.medium - 1}px)`)
    this.isMobile = this.mobileQuery.matches
    this.mobileQuery.addEventListener('change', this.handleMobileQueryChange)
    this.addEventListener('click', this.handleTileClick)
    this.addEventListener(ProductAddedEvent.type, this.handleProductAdded)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.mobileQuery?.removeEventListener('change', this.handleMobileQueryChange)
    this.removeEventListener('click', this.handleTileClick)
    this.removeEventListener(ProductAddedEvent.type, this.handleProductAdded)
    if (this.footerHeightMeasureHandle !== null) cancelAnimationFrame(this.footerHeightMeasureHandle)
  }

  private lastReportedFooterHeights: number[] | null = null
  private footerHeightMeasureHandle: number | null = null

  private scheduleFooterHeightReport() {
    if (this.footerHeightMeasureHandle !== null) return
    this.footerHeightMeasureHandle = requestAnimationFrame(() => {
      this.footerHeightMeasureHandle = null
      this.reportFooterHeight()
    })
  }

  private reportFooterHeight() {
    const rows = this.shadowRoot?.querySelectorAll<HTMLElement>('.product-tile-footer .zone-row')
    if (!rows || rows.length === 0) return

    const heights = [...rows].map(row => {
      const zoneHeights = [...row.querySelectorAll<HTMLElement>(':scope > .zone')].map(zone => zone.getBoundingClientRect().height)
      return zoneHeights.length ? Math.max(...zoneHeights) : 0
    })

    if (heights.every(h => h <= 0) || arraysEqual(heights, this.lastReportedFooterHeights)) return

    this.lastReportedFooterHeights = heights
    this.dispatchEvent(new ProductTileFooterResizedEvent(heights))
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)
    // Row-alignment only makes sense for the grid card variant — 'wide'/'inline' (list view)
    // render one tile per row, full-width, with no sibling beside it to align against.
    if (this.cardVariant !== 'default') return
    if (changedProperties.size === 1 && changedProperties.has('footerRowMinHeights')) return
    this.scheduleFooterHeightReport()
  }

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  private visibleWidgets(widgets: SimpleWidgetCfg[] | undefined): SimpleWidgetCfg[] {
    return (widgets ?? []).filter(w => isWidgetVisible(w, this.product))
  }

  private hasSectionContent(section: DynamicSectionCfg): boolean {
    return Object.values(section.zones ?? {}).some(zone => this.visibleWidgets(zone.widgets).length > 0) || this.hasCustomPadding(section)
  }

  // A configured area padding must render as its own box even with no widgets placed yet —
  // otherwise the padding an author set would silently have no visible effect.
  private hasCustomPadding(cfg: AreaPaddingCfg | undefined): boolean {
    return !!(cfg && (cfg.paddingTop || cfg.paddingRight || cfg.paddingBottom || cfg.paddingLeft))
  }

  private paddingStyleFor(cfg: AreaPaddingCfg | undefined): string {
    if (!cfg) return ''
    return [
      ...(cfg.paddingTop ? [`padding-top:${cfg.paddingTop}`] : []),
      ...(cfg.paddingRight ? [`padding-right:${cfg.paddingRight}`] : []),
      ...(cfg.paddingBottom ? [`padding-bottom:${cfg.paddingBottom}`] : []),
      ...(cfg.paddingLeft ? [`padding-left:${cfg.paddingLeft}`] : []),
    ].join(';')
  }

  // For the three cse-card slots (header/body/footer) specifically — unlike paddingStyleFor
  // above (still used for image-above/below, which have no cse-card slot of their own to
  // route through) — area padding is applied by setting cse-card's own
  // --cse-card-{slot}-padding-y/x custom properties (inline on <cse-card> itself, in
  // cardSlotStyle below) rather than literal padding-* on the slotted div. Two reasons: it's
  // the same mechanism the theme's own (unconfigured) defaults already use, so a configured
  // value simply replaces rather than needing separately zeroing them; and card-variant="wide"/
  // "inline" gives .product-tile-header a hard-coded width + flex: 0 0 <width> for the
  // fixed-width side-by-side image column, and literal padding-left/right on that same
  // fixed-basis box interacts badly with it — the cse-card slot wrapper this targets instead
  // sits outside that fixed-width box entirely, so it doesn't hit the conflict. -y/-x are each
  // a 2-value shorthand (CSS logical padding-block/padding-inline order: start then end, i.e.
  // top-then-bottom and left-then-right for LTR) — unset sides default to 0 once any side on
  // this area is configured, same as paddingStyleFor already does by simply omitting them.
  private cardSlotPaddingVars(slot: 'header' | 'body' | 'footer', cfg: AreaPaddingCfg | undefined): string[] {
    if (!this.hasCustomPadding(cfg)) return []
    const top = cfg?.paddingTop || '0'
    const right = cfg?.paddingRight || '0'
    const bottom = cfg?.paddingBottom || '0'
    const left = cfg?.paddingLeft || '0'
    return [
      `--cse-card-${slot}-padding-y:${top} ${bottom}`,
      `--cse-card-${slot}-padding-x:${left} ${right}`,
    ]
  }

  // Mobile list (inline) stacks productInfo over orderActions, and productInfo grows by default
  // (:host's --cse-card-body-flex-grow) so orderActions sits at the bottom. orderActions' own
  // verticalAlign 'flex-start' opts out: both areas keep their content height, stacked at the top.
  private stacksAreasAtTop(orderActions: DynamicSectionCfg): boolean {
    return this.cardVariant === 'inline' && orderActions.verticalAlign === 'flex-start'
  }

  private getActiveImageConfig(): ImageDisplayConfig | undefined {
    if (!this.tileConfig) return undefined
    return resolveLayoutConfig(this.tileConfig, this.cardVariant, this.isMobile).image
  }

  // Product tile images always render at the 'small' rendition — there is no per-tile
  // size configuration (design decision: consistent image size everywhere).
  private getPrimarySource(): string {
    return this.product.picture.small
  }

  private getPrimaryFallbackSource(): string {
    return this.product.defaultPicture.small
  }

  private getSecondarySource(): string | undefined {
    if (this.getActiveImageConfig()?.enableHoverImage !== true) return undefined
    const pic = this.product.secondaryPicture
    if (!pic) return undefined
    return pic.small ?? pic.src ?? undefined
  }

  private renderSimpleWidget(w: SimpleWidgetCfg) {
    const widgetElement = buildWidgetElement(w, { product: this.product, locale: this.locale, index: this.index })
    if (!widgetElement) return nothing
    return html`<slot name="${w.type}">${widgetElement}</slot>`
  }

  private renderSimpleImageOverlays(section: ImageSectionCfg) {
    return html`
      ${PLACES_8.map(place => html`
        <div class="image-area ${place} ${section.hideUntilHoverPlaces?.[place] && !this.isMobile ? 'hover-reveal' : ''}">
          ${this.visibleWidgets(section[place]).map(w => html`<div class="widget-instance">${this.renderSimpleWidget(w)}</div>`) }
        </div>
      `)}
    `
  }

  // above/below are a real (if always-singular) dynamic zone each — reuses renderZone (the
  // same helper productInfo/orderActions zones use) for the widgets themselves, wrapped in
  // its own padding-carrying element, same outer/inner split renderDynamicSection uses for
  // .content/.zone-row (padding on the outer wrapper, flex settings on the inner .zone). A
  // configured padding must still render as its own box even with no widgets placed yet —
  // same reasoning as hasCustomPadding for productInfo/orderActions.
  private renderImageFlowZone(image: ImageSectionCfg, slot: 'above' | 'below') {
    const zoneCfg = image[slot]
    if (!zoneCfg) return nothing
    if (!this.visibleWidgets(zoneCfg.widgets).length && !this.hasCustomPadding(zoneCfg)) return nothing
    const className = slot === 'above' ? 'image-above' : 'image-below'
    return html`
      <div class="${className}" style="${this.paddingStyleFor(zoneCfg)}">
        ${this.renderZone(zoneCfg)}
      </div>
    `
  }

  private renderZone(zone: ZoneCfg) {
    const widgets = this.visibleWidgets(zone.widgets)
    if (!widgets.length) return nothing
    const direction: FlexDirection = zone.direction ?? 'column'
    const styleParts = [
      `flex-direction:${direction}`,
      `justify-content:${zone.justifyContent ?? 'flex-start'}`,
      `align-items:${zone.alignItems ?? 'stretch'}`,
      `flex-wrap:${zone.wrap ? 'wrap' : 'nowrap'}`,
      ...(zone.gap ? [`gap:${zone.gap}`] : []),
      ...(zone.rowGap ? [`row-gap:${zone.rowGap}`] : []),
      ...(zone.columnGap ? [`column-gap:${zone.columnGap}`] : []),
    ]
    return html`<div class="zone" style="${styleParts.join(';')}">${widgets.map(w => html`<div class="widget-instance">${this.renderSimpleWidget(w)}</div>`)}</div>`
  }

  // alignRows: footer row alignment (see footerRowMinHeights) pairs rows across tiles by their
  // rendered index, so a row whose widgets are all hidden by rules for this product must still
  // render (as an empty .zone-row) — otherwise every row below it shifts up one index and gets
  // another row's reserved height. It stays display:none until a sibling tile reports real
  // content for that index, so a row hidden in every tile adds no stray gap.
  private renderDynamicSection(section: DynamicSectionCfg, rowMinHeights?: number[] | null, alignRows = false) {
    const zones = section.zones ?? {}
    const rowHasConfiguredWidgets = (zoneIds: readonly string[]): boolean => zoneIds.some(id => (zones[id]?.widgets?.length ?? 0) > 0)
    const rowHasVisibleWidgets = (zoneIds: readonly string[]): boolean => zoneIds.some(id => this.visibleWidgets(zones[id]?.widgets).length > 0)
    // Unset falls through to the CSS default (spacing05, see .content/.zone-row in the
    // stylesheet) — only emit an inline override when an author actually configured one,
    // same pattern as area padding (paddingStyleFor).
    const rowGapStyle = section.rowGap ? `gap:${section.rowGap}` : ''
    const columnGapStyle = section.columnGap ? `gap:${section.columnGap}` : ''
    const verticalAlignStyle = section.verticalAlign && this.cardVariant !== 'default' ? `justify-content:${section.verticalAlign}` : ''
    const contentStyle = [rowGapStyle, verticalAlignStyle].filter(Boolean).join(';')

    let renderedRowIndex = -1
    return html`
      <div class="section-content content" style="${contentStyle}">
        ${(section.layout ?? []).map(row => {
          const hasVisibleWidgets = rowHasVisibleWidgets(row)
          const keepAsRuleHiddenRow = alignRows && !hasVisibleWidgets && rowHasConfiguredWidgets(row)
          if (!hasVisibleWidgets && !keepAsRuleHiddenRow) return nothing
          renderedRowIndex++
          const minHeight = rowMinHeights?.[renderedRowIndex]
          const collapsed = keepAsRuleHiddenRow && !(minHeight && minHeight > 0)
          const rowStyle = [columnGapStyle, minHeight != null ? `min-height:${minHeight}px` : '', collapsed ? 'display:none' : ''].filter(Boolean).join(';')
          const rowZones = row.map(id => zones[id]).filter((z): z is ZoneCfg => !!z)
          return html`<div class="zone-row ${row.length === 1 ? 'full-width' : 'split'}" style="${rowStyle}">${rowZones.map(z => this.renderZone(z))}</div>`
        })}
      </div>
    `
  }

  private renderFromSimpleLayout(cfg: SimpleLayoutCfg) {
    if (this.loading) {
      return this.renderSkeletonLoading()
    }

    const productInfo = cfg.productInfo ?? {}
    const orderActions = cfg.orderActions ?? {}

    const imageConfig = this.getActiveImageConfig()

    // A configured area padding is meant to be the *only* padding on that area — replacing
    // (not stacking on top of) whatever the theme's own defaults contribute. Setting the real
    // values into cse-card's own --cse-card-{header,body,footer}-padding-y/x (see
    // cardSlotPaddingVars) already achieves that — it's the exact same custom property the
    // theme's own unconfigured default flows through, just with the author's value instead.
    const cardStyle = [
      ...this.cardSlotPaddingVars('header', imageConfig),
      ...this.cardSlotPaddingVars('body', productInfo),
      ...this.cardSlotPaddingVars('footer', orderActions),
      ...(this.stacksAreasAtTop(orderActions) ? ['--cse-card-body-flex-grow:0'] : []),
    ].join(';')

    // .product-tile-header still carries its own legacy padding-top/bottom in the stylesheet
    // (base-product-tile.style.ts, for themes still on the old --csc-base-product-tile-*
    // header-padding-y/image-padding-y vars) — zero it inline here whenever tile-config
    // configures the image area's own padding, so that (now routed through
    // --cse-card-header-padding-y above) is the only one that applies, not both stacked.
    const headerStyle = this.hasCustomPadding(imageConfig) ? 'padding-top:0;padding-bottom:0' : ''

    return html`
      <cse-card variant="${this.cardVariant}" style="${cardStyle}">
        <div slot="header" class="product-tile-header" style="${headerStyle}">
          ${this.cardVariant === 'wide' || this.cardVariant === 'inline' ? this.renderImageFlowZone(cfg.image, 'above') : nothing}
          <div class="image-wrapper">
            <csc-product-image
              source="${this.getPrimarySource()}"
              fallback-source="${this.getPrimaryFallbackSource()}"
              secondary-source="${ifDefined(this.getSecondarySource())}"
              alt="${this.product.name}"
              url="${this.product.pageLink}"
              fit="${ifDefined(imageConfig?.fit)}"
              blend-mode="${ifDefined(imageConfig?.blendMode)}"
            ></csc-product-image>
            ${this.renderSimpleImageOverlays(cfg.image)}
          </div>
          ${this.cardVariant === 'wide' || this.cardVariant === 'inline' ? this.renderImageFlowZone(cfg.image, 'below') : nothing}
        </div>

        ${this.hasSectionContent(productInfo) ? html`
          <div slot="body" class="product-tile-body">
            ${this.renderDynamicSection(productInfo)}
          </div>
        ` : nothing}

        ${this.hasSectionContent(orderActions) ? html`
          <div slot="footer" class="product-tile-footer">
            ${this.renderDynamicSection(orderActions, this.cardVariant === 'default' ? this.footerRowMinHeights : null, this.cardVariant === 'default')}
          </div>
        ` : nothing}
      </cse-card>
    `
  }

  override render() {
    if (this.loading) {
      return this.renderSkeletonLoading()
    }

    return this.renderFromSimpleLayout(resolveLayoutConfig(this.tileConfig, this.cardVariant, this.isMobile))
  }

  redirect() {
    this.windowLocation.redirect(
      new URL(this.product.pageLink, window.location.origin + window.location.pathname)
    )
  }

  private resolvePathname(url: string): string | null {
    try {
      return new URL(url, window.location.origin + window.location.pathname).pathname
    } catch {
      return null
    }
  }

  private isProductPageAnchor(anchor: HTMLAnchorElement): boolean {
    if (!this.product.pageLink) return false
    const href = anchor.getAttribute('href')
    if (!href) return false
    const anchorPathname = this.resolvePathname(href)
    if (anchorPathname === null) return false
    return anchorPathname === this.resolvePathname(this.product.pageLink)
  }

  private handleTileClick = (event: MouseEvent) => {
    const anchor = event.composedPath().find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement)
    if (!anchor || !this.isProductPageAnchor(anchor)) return
    track({ type: 'product-selected', product: this.product, context: { ...(this.index !== undefined && { index: this.index }) } })
  }

  private handleProductAdded = (event: ProductAddedEvent) => {
    if (!event.addedQuantity || event.addedQuantity <= 0) return
    track({ type: 'product-added-to-basket', product: this.product, quantity: event.addedQuantity, context: { ...(this.index !== undefined && { index: this.index }) } })
  }

  private renderSkeletonLoading() {
    const isListView = this.cardVariant === 'wide' || this.cardVariant === 'inline'
    const defaultHeight = isListView ? 220 : 400
    const height = this.skeletonHeight ?? defaultHeight
    return html`
      <cse-skeleton
        class="skeleton-loading"
        variant=${this.skeletonVariant}
        height=${height}
        index=${ifDefined(0)}>
      </cse-skeleton>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-base-product-tile': CSCBaseProductTile
  }
}
